import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GearEditor } from '@/components/GearEditor';
import { IkoCertEditor } from '@/components/IkoCertEditor';
import { UnitToggle } from '@/components/UnitToggle';
import { resolveUnits, weightUnit, displayWeight, weightToKg, weightPlaceholder } from '@/lib/units';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check, Loader2, Gauge, MapPin, Package, Award } from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = ['beginner', 'intermediate', 'advanced'];

export default function Onboarding() {
  const { refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [levelIdx, setLevelIdx] = useState(0);
  const [spots, setSpots] = useState([]);
  const [favSpot, setFavSpot] = useState('');
  const [country, setCountry] = useState('');
  const [gear, setGear] = useState([{ type: 'kite', brand: '', size: '', model: '' }]);
  const [units, setUnits] = useState(resolveUnits(null));
  const [weightKg, setWeightKg] = useState(null);
  const [ikoCertified, setIkoCertified] = useState(null); // null = not chosen yet
  const [ikoCerts, setIkoCerts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/spots').then(({ data }) => setSpots(data)).catch(() => {}); }, []);

  const onUnitChange = (u) => {
    setUnits(u);
    if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', u);
  };

  const finish = async () => {
    setBusy(true);
    try {
      const equipment = gear.filter((g) => g.brand || g.size || g.model);
      await api.put('/auth/onboarding', {
        skill_level: LEVELS[levelIdx], favorite_spot_id: favSpot || null,
        home_country: country, equipment, weight_kg: weightKg, units,
        iko_certified: ikoCertified,
        iko_certifications: ikoCertified ? ikoCerts.filter((c) => c.cert) : [],
      });
      if (typeof localStorage !== 'undefined') localStorage.setItem('kiteiq_units', units);
      await refreshUser();
      toast.success(t('onboarding.savedToast'));
      navigate('/analyze');
    } catch (e) {
      toast.error(t('onboarding.saveError'));
    } finally { setBusy(false); }
  };

  const curLevel = LEVELS[levelIdx];

  const steps = [
    {
      icon: Gauge, title: t('onboarding.step1Title'), sub: t('onboarding.step1Sub'),
      body: (
        <div className="mt-8">
          <div className="font-display text-5xl text-[#48CAE4]">{t(`onboarding.levels.${curLevel}`)}</div>
          <p className="text-muted-foreground mt-2">{t(`onboarding.levelDesc.${curLevel}`)}</p>
          <div className="mt-8" data-testid="onboarding-skill-slider">
            <Slider value={[levelIdx]} min={0} max={2} step={1} onValueChange={(v) => setLevelIdx(v[0])} />
            <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>{t('onboarding.sliderBeginner')}</span><span>{t('onboarding.sliderIntermediate')}</span><span>{t('onboarding.sliderAdvanced')}</span></div>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 space-y-4">
            <div>
              <Label className="text-xs text-muted-foreground">{t('onboarding.preferredUnits')}</Label>
              <div className="mt-1.5"><UnitToggle value={units} onChange={onUnitChange} testid="onboarding-unit-toggle" /></div>
            </div>
            <div>
              <Label htmlFor="ob-weight">{t('onboarding.weightLabel', { unit: weightUnit(units) })} <span className="text-muted-foreground text-xs">{t('onboarding.weightHint')}</span></Label>
              <Input id="ob-weight" type="number" inputMode="decimal"
                value={displayWeight(weightKg, units) ?? ''}
                onChange={(e) => setWeightKg(weightToKg(e.target.value, units))}
                placeholder={weightPlaceholder(units)}
                className="mt-1.5 bg-[#12161B] border-white/15" data-testid="onboarding-weight" />
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: MapPin, title: t('onboarding.step2Title'), sub: t('onboarding.step2Sub'),
      body: (
        <div className="mt-8 space-y-4">
          <div>
            <Label>{t('onboarding.favoriteSpot')}</Label>
            <Select value={favSpot} onValueChange={setFavSpot}>
              <SelectTrigger className="mt-1.5 bg-[#12161B] border-white/15" data-testid="onboarding-spot-select"><SelectValue placeholder={t('onboarding.chooseSpot')} /></SelectTrigger>
              <SelectContent className="bg-[#12161B] border-white/15 text-white max-h-72">
                {spots.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.country}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="country">{t('onboarding.homeCountry')}</Label>
            <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder={t('onboarding.countryPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="onboarding-country" />
          </div>
        </div>
      ),
    },
    {
      icon: Package, title: t('onboarding.step3Title'), sub: t('onboarding.step3Sub'),
      body: (
        <div className="mt-6">
          <GearEditor value={gear} onChange={setGear} />
        </div>
      ),
    },
    {
      icon: Award, title: t('onboarding.step4Title'), sub: t('onboarding.step4Sub'),
      body: (
        <div className="mt-6">
          <IkoCertEditor
            certified={ikoCertified}
            certs={ikoCerts}
            onChange={({ certified, certs }) => { setIkoCertified(certified); setIkoCerts(certs); }}
          />
          <p className="text-xs text-muted-foreground mt-4">{t('onboarding.ikoOptional')}</p>
        </div>
      ),
    },
  ];

  const cur = steps[step];
  const Icon = cur.icon;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className={`w-full transition-all ${step >= 2 ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="flex gap-2 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-[#00B4D8]' : 'bg-white/10'}`} />
          ))}
        </div>
        <div className="kite-card p-7">
          <div className="w-12 h-12 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center"><Icon className="w-6 h-6 text-[#00B4D8]" /></div>
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }}>
              <h1 className="text-2xl font-semibold mt-5">{cur.title}</h1>
              <p className="text-muted-foreground text-sm mt-1">{cur.sub}</p>
              {cur.body}
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center justify-between mt-8">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="text-muted-foreground hover:text-white" data-testid="onboarding-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t('onboarding.back')}
            </Button>
            {step < steps.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="onboarding-next">
                {t('onboarding.next')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={busy} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="onboarding-finish">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t('onboarding.startRiding')} <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
